import {useEffect, useRef} from "react";
import * as Y from "yjs";
import {
  VisionBoardEdge,
  VisionBoardEdgeId,
  VisionBoardNode,
  VisionBoardNodeId,
} from "../../cdx-models/utils/extended-fields";
import {VisionBoardId} from "../../cdx-models/VisionBoard";
import {getBoundingBox, VisionBoardBag} from "./canvas-utils";
import {NodeBushEntry} from "./useVisionBoardData";
import {duplicateVisionBoardNode} from "./canvas-node-utils";

let fallbackClipboard: string | null = null;

const writeToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (e: unknown) {
    console.warn(e);
    fallbackClipboard = text;
  }
};

const readFromClipboard = async () => {
  try {
    return await navigator.clipboard.readText();
  } catch {
    return fallbackClipboard;
  }
};

type ClipboardData = {
  format: "cdx-vision-board";
  boardId: VisionBoardId;
  nodeList: {node: VisionBoardNode; id: VisionBoardNodeId}[];
  edgeList: {edge: VisionBoardEdge; id: VisionBoardEdgeId}[];
};

export const copyNodes = (
  nodes: NodeBushEntry[],
  bag: VisionBoardBag,
  type: ClipboardEvent["type"]
) => {
  const idSet = new Set(nodes.map((n) => n.id));
  const bounds = getBoundingBox(nodes);
  const allEdges = bag.store.getEdgesSnaphot(bounds);
  const edges = allEdges.filter((e) => idSet.has(e.source.node.id) && idSet.has(e.target.node.id));
  const data: ClipboardData = {
    format: "cdx-vision-board",
    boardId: bag.visionBoard.id as VisionBoardId,
    nodeList: nodes.map((n) => ({node: n.node.toJSON() as VisionBoardNode, id: n.id})),
    edgeList: edges.map((e) => ({edge: e.edge.toJSON() as VisionBoardEdge, id: e.id})),
  };
  if (type === "cut") {
    bag.store.doc.transact(() => {
      nodes.forEach((n) => (n.node.parent as Y.Map<any>).delete(n.id));
      edges.forEach((e) => (e.edge.parent as Y.Map<any>).delete(e.id));
    });
  }
  writeToClipboard(JSON.stringify(data));
};

export const pasteNodes = async (bag: VisionBoardBag) => {
  const getData = async () => {
    const text = await readFromClipboard();
    if (!text) return null;
    try {
      const content = JSON.parse(text) as ClipboardData;
      if (content.format === "cdx-vision-board") return content;
    } catch {}
    return null;
  };

  const data = await getData();
  if (!data) return;
  const {nodeList, edgeList} = data;
  const oldToNewIds = new Map<VisionBoardNodeId, VisionBoardNodeId>();
  const getNextPos = () => {
    const currView = bag.bboxStore.getSnapshot();
    const minX = Math.min(...nodeList.map((el) => el.node.pos.x));
    const minY = Math.min(...nodeList.map((el) => el.node.pos.y));
    if (data.boardId === bag.visionBoard.id) {
      if (
        minX > currView.minX &&
        minX < currView.maxX - 20 &&
        minY > currView.minY &&
        minY < currView.maxY - 20
      ) {
        // old nodes within current view, just use cmd+d behaviour of 20px offset
        return null;
      }
    }
    const maxX = Math.max(...nodeList.map((el) => el.node.pos.x + el.node.pos.w));
    const maxY = Math.max(...nodeList.map((el) => el.node.pos.y + el.node.pos.h));
    const w = maxX - minX;
    const h = maxY - minY;
    const nextX = currView.minX + Math.max(20, (currView.maxX - currView.minX - w) / 2);
    const nextY = currView.minY + Math.max(20, (currView.maxY - currView.minY - h) / 2);
    const offsetX = nextX - minX;
    const offsetY = nextY - minY;

    return [offsetX, offsetY];
  };
  const offset = getNextPos();
  const nextIds = bag.store.doc.transact(() => {
    const newNodeIds = nodeList.map(({node, id}) => {
      const newId = duplicateVisionBoardNode(
        bag,
        node,
        offset ? [node.pos.x + offset[0], node.pos.y + offset[1]] : undefined
      ).id;
      oldToNewIds.set(id, newId);
      return newId;
    });
    edgeList.forEach(({edge}) => {
      const newSourceId = oldToNewIds.get(edge.value.source.nodeId)!;
      const newTargetId = oldToNewIds.get(edge.value.target.nodeId)!;
      if (newSourceId && newTargetId) {
        bag.store.addEdge({
          source: {nodeId: newSourceId, side: edge.value.source.side},
          target: {nodeId: newTargetId, side: edge.value.target.side},
        });
      }
    });
    return newNodeIds;
  });
  bag.selectionStore.setNodeIds(nextIds);
};

export const useCopyPaste = (bag: VisionBoardBag) => {
  const somethingSelected = bag.selectionStore.useSelectedNodes((ids) => ids.size > 0);
  const bagRef = useRef(bag);
  bagRef.current = bag;
  useEffect(() => {
    const pasteHandler = (e: ClipboardEvent) => {
      e.preventDefault();
      pasteNodes(bagRef.current);
    };
    document.addEventListener("paste", pasteHandler);
    return () => {
      document.removeEventListener("paste", pasteHandler);
    };
  }, []);
  useEffect(() => {
    if (!somethingSelected) return;
    const copyHandler = (e: ClipboardEvent) => {
      if (e.clipboardData && e.clipboardData.types.length > 1) return;
      e.preventDefault();
      const selIds = bagRef.current.selectionStore.getSelectedNodeIdsSnapshot();
      const nodes = [...selIds].map((id) => bagRef.current.store.getNode(id)!).filter(Boolean);
      copyNodes(nodes, bagRef.current, e.type);
    };
    document.addEventListener("copy", copyHandler);
    document.addEventListener("cut", copyHandler);
    return () => {
      document.removeEventListener("copy", copyHandler);
      document.removeEventListener("cut", copyHandler);
    };
  }, [somethingSelected]);
};
